import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

import apiClient from "../services/apiClient";

import Cookies from "js-cookie";

const CartContext = createContext();

const LOCAL_CART_KEY = "cartItems";

const isAuthenticated = () => !!Cookies.get("authToken");

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);

  // --- Helpers ---

  const getLocalCart = () => {
    try {
      const saved = localStorage.getItem(LOCAL_CART_KEY);

      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  };

  const saveLocalCart = (newItems) => {
    localStorage.setItem(LOCAL_CART_KEY, JSON.stringify(newItems));

    setItems(newItems);
  };

  const normalizeItem = (item, isApi = true) => {
    if (isApi) {
      if (!item.product) return null;

      return {
        id: item.id,

        productId: item.product_id,

        quantity: parseInt(item.quantity, 10) || 1,

        variations:
          item.variations?.map((v) => ({ name: v.name, value: v.value })) || [],

        product: {
          id: item.product?.id,

          title: item.product?.title,

          price: parseFloat(item.product?.price) || 0,

          image:
            item.product?.images?.[0]?.image_path || "/images/placeholder.jpg",
        },
      };
    }

    return item;
  };

  // --- Core Actions ---

  const loadCart = useCallback(async () => {
    setLoading(true);

    try {
      if (isAuthenticated()) {
        const res = await apiClient.get("/api/cart");

        if (res.data.type === "success") {
          const validItems = res.data.message.data

            .map((i) => normalizeItem(i, true))

            .filter((item) => item !== null);

          setItems(validItems);
        }
      } else {
        const local = getLocalCart().filter(
          (i) => i && i.product && i.product.price !== undefined,
        );

        setItems(local);
      }
    } catch (err) {
      setError("Failed to load cart");
    } finally {
      setLoading(false);
    }
  }, []);

  // --- AUTO-SYNC (The fix for 404s) ---

  useEffect(() => {
    const syncLocalToApi = async () => {
      const localData = getLocalCart();

      if (isAuthenticated() && localData.length > 0) {
        for (const item of localData) {
          try {
            await apiClient.post("/api/cart", {
              product_id: item.productId,

              quantity: item.quantity,

              variations: item.variations,
            });
          } catch (e) {
            // Silently fail to maintain cart continuity
          }
        }

        localStorage.removeItem(LOCAL_CART_KEY);

        loadCart(); // Refresh to get real database IDs
      }
    };

    syncLocalToApi();
  }, [loadCart]);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const updateQuantity = async (itemId, newQty) => {
    if (newQty < 1) return removeItem(itemId);

    // FIX: Check if the ID is a local string (e.g., "local_123")

    const isLocalId = String(itemId).startsWith("local_");

    const previousItems = [...items];

    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, quantity: newQty } : item,
      ),
    );

    try {
      // Only call API if logged in AND it's a real database ID

      if (isAuthenticated() && !isLocalId) {
        const item = items.find((i) => i.id === itemId);

        await apiClient.put(`/api/cart/${itemId}`, {
          product_id: item.productId,

          quantity: newQty,

          variations: item.variations,
        });
      } else {
        const local = getLocalCart().map((i) =>
          i.id === itemId ? { ...i, quantity: newQty } : i,
        );

        saveLocalCart(local);
      }

      return { success: true };
    } catch (err) {
      setItems(previousItems);
      const errorMsg = err.response?.data?.message || err.message || "Failed to update";
      return { success: false, error: errorMsg };
    }
  };

  const removeItem = async (itemId) => {
    const isLocalId = String(itemId).startsWith("local_");

    const previousItems = [...items];

    setItems((prev) => prev.filter((i) => i.id !== itemId));

    try {
      if (isAuthenticated() && !isLocalId) {
        await apiClient.delete(`/api/cart/${itemId}`);
      } else {
        const local = getLocalCart().filter((i) => i.id !== itemId);

        saveLocalCart(local);
      }

      return { success: true };
    } catch (err) {
      setItems(previousItems);
      const errorMsg = err.response?.data?.message || err.message || "Failed to remove";
      return { success: false, error: errorMsg };
    }
  };

  const addItem = async (product, quantity = 1, variations = []) => {
    try {
      if (isAuthenticated()) {
        await apiClient.post("/api/cart", {
          product_id: product.id,
          quantity,
          variations,
        });

        await loadCart();
      } else {
        const local = getLocalCart();

        const existingIndex = local.findIndex(
          (i) =>
            i.productId === product.id &&
            JSON.stringify(i.variations) === JSON.stringify(variations),
        );

        if (existingIndex >= 0) {
          local[existingIndex].quantity += quantity;
        } else {
          local.push({
            id: `local_${Date.now()}`,

            productId: product.id,

            quantity,

            variations,

            product: {
              id: product.id,

              title: product.title || product.name,

              price: parseFloat(product.price) || 0,

              image: product.image || product.images?.[0],
            },
          });
        }

        saveLocalCart(local);
      }

      return { success: true };
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || "Failed to add";
      return { success: false, error: errorMsg };
    }
  };

  const subtotal = items.reduce((acc, item) => {
    const price = parseFloat(item?.product?.price) || 0;

    const qty = parseInt(item?.quantity) || 0;

    return acc + price * qty;
  }, 0);

  const itemCount = items.reduce(
    (acc, item) => acc + (parseInt(item?.quantity) || 0),
    0,
  );

  // Clear cart function

  const clearCart = () => {
    setItems([]);

    localStorage.removeItem(LOCAL_CART_KEY);
  };

  return (
    <CartContext.Provider
      value={{
        items,
        loading,
        error,
        addItem,
        updateQuantity,
        removeItem,
        loadCart,
        clearCart,
        subtotal,
        itemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
